import type { ReactNode } from "react";
import { toast } from "sonner";

import FuelIcon from "@/assets/icons/fuel.svg?react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { formatNumber } from "@/lib/format";

/**
 * Лаймовое свечение на подложке шапки (Figma 2440:12149).
 * Направление и альфа-стопы градиента сняты с пикселей экспортированного ассета,
 * цвет — токен --color-primary-500; шум воспроизводится отдельным SVG-фильтром.
 */
const HEADER_GLOW = [
  "linear-gradient(117deg,",
  "transparent 12%,",
  "color-mix(in srgb, var(--color-primary-500) 1%, transparent) 25%,",
  "color-mix(in srgb, var(--color-primary-500) 4%, transparent) 44%,",
  "color-mix(in srgb, var(--color-primary-500) 6%, transparent) 50%,",
  "color-mix(in srgb, var(--color-primary-500) 9%, transparent) 56%,",
  "color-mix(in srgb, var(--color-primary-500) 12%, transparent) 66%,",
  "color-mix(in srgb, var(--color-primary-500) 18%, transparent) 72%,",
  "color-mix(in srgb, var(--color-primary-500) 24%, transparent) 78%,",
  "color-mix(in srgb, var(--color-primary-500) 34%, transparent) 88%,",
  "color-mix(in srgb, var(--color-primary-500) 48%, transparent) 94%,",
  "color-mix(in srgb, var(--color-primary-500) 80%, transparent) 100%)",
].join(" ");

// ============ Referral Stat ============

interface ReferralStatProps {
  value: ReactNode;
  label: string;
}

const ReferralStat = ({ value, label }: ReferralStatProps) => (
  <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5 whitespace-nowrap">
    <p className="fs-500 font-bold text-neutral-50">{value}</p>
    <p className="fs-300 font-semibold text-neutral-50/22">{label}</p>
  </div>
);

// ============ Loading Skeleton ============

const ReferralCardSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      // Layout
      "flex w-full flex-col items-start justify-center gap-6 p-4",
      // Background & Border
      "rounded-mds border border-neutral-50/7 bg-neutral-50/2",
      className
    )}
  >
    <Skeleton className="h-34 w-full rounded-[1rem] bg-neutral-50/5" />

    <div className="flex w-full flex-col items-start gap-2">
      <Skeleton className="h-12 w-full rounded-[1rem] bg-neutral-50/5" />
      <Skeleton className="h-5 w-3/4 bg-neutral-50/5" />
    </div>

    <div className="flex w-full items-center justify-between gap-4">
      <Skeleton className="h-10 flex-1 bg-neutral-50/5" />
      <Skeleton className="h-10 flex-1 bg-neutral-50/5" />
    </div>
  </div>
);

// ============ Referral Card ============

export interface ReferralCardProps {
  link: string;
  fuelRewardPercent: number;
  invitedCount: number;
  earnedFuel: string;
  isLoading?: boolean;
  className?: string;
}

export const ReferralCard = ({
  link,
  fuelRewardPercent,
  invitedCount,
  earnedFuel,
  isLoading = false,
  className,
}: ReferralCardProps) => {
  // Аннотация в Figma (2440:12162): «Когда нет данных — блок не отображается»
  const hasStats = invitedCount > 0 || Number(earnedFuel) > 0;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
  };

  if (isLoading) {
    return <ReferralCardSkeleton className={className} />;
  }

  return (
    <div
      className={cn(
        // Layout
        "flex w-full flex-col items-start justify-center gap-6 p-4",
        // Background & Border
        "rounded-mds border border-neutral-50/7 bg-neutral-50/2",
        className
      )}
    >
      {/* Шапка карточки с лаймовым свечением (2440:12149) */}
      <div
        className={cn(
          // Layout
          "relative flex h-34 w-full flex-col items-start justify-between overflow-hidden p-4",
          // Background & Border
          "rounded-mds border-primary-500/10 border bg-neutral-50/2"
        )}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: HEADER_GLOW }}
        />
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 size-full opacity-[0.12] mix-blend-soft-light"
        >
          <filter id="referral-header-noise">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.37"
              numOctaves={3}
              seed={5508}
              stitchTiles="stitch"
            />
          </filter>
          <rect
            width="100%"
            height="100%"
            filter="url(#referral-header-noise)"
          />
        </svg>

        <div className="relative flex items-center gap-3">
          <Icon
            name="referral"
            className="size-5.5 text-white"
          />
          <p className="fs-600 font-bold text-white">Реферальная система</p>
        </div>

        <div className="relative flex items-center justify-center rounded-sm bg-neutral-50/5 px-3 py-1">
          <p className="fs-300 font-semibold whitespace-nowrap text-neutral-100">
            {fuelRewardPercent}% от сожжённого топлива
          </p>
        </div>
      </div>

      {/* Реферальная ссылка + описание (2440:12155) */}
      <div className="flex w-full flex-col items-start gap-2">
        <div
          className={cn(
            // Layout
            "flex w-full items-center gap-4 py-1 pr-1 pl-3",
            // Background & Border
            "rounded-mds bg-neutral-50/2"
          )}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Icon
              name="link"
              className="size-5 text-neutral-50/22"
            />
            <p className="fs-200 truncate font-medium text-neutral-50/80">{link}</p>
          </div>

          <Button
            variant="neutral"
            size="sm"
            className="fs-300 shrink-0"
            onClick={handleCopy}
          >
            <Icon
              name="copy"
              className="size-5"
            />
            Копировать
          </Button>
        </div>

        <p className="fs-200 w-full font-medium text-neutral-50/22">
          Каждый пользователь, зарегистрированный по твоей ссылке, позволит тебе получать{" "}
          {fuelRewardPercent}% топлива от его успешных сделок
        </p>
      </div>

      {/* Статистика рефералов (2440:12162) */}
      {hasStats && (
        <div className="flex w-full items-center justify-between">
          <ReferralStat
            value={formatNumber(invitedCount, 0)}
            label="Приглашено"
          />

          <div className="h-[2.4375rem] w-px shrink-0 bg-neutral-50/7" />

          <ReferralStat
            value={
              <span className="inline-flex items-center gap-1">
                {formatNumber(Number(earnedFuel))}
                <FuelIcon
                  aria-label="Топливо"
                  className="size-4 shrink-0"
                />
              </span>
            }
            label="Заработано"
          />
        </div>
      )}
    </div>
  );
};
